import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  Subscription,
  distinctUntilChanged,
  map,
  switchMap,
  take,
} from 'rxjs';
import { Store } from '@ngrx/store';

import {
  ButtonLayout,
  ButtonMode,
  ButtonPosition,
  ButtonTheme,
  ButtonType,
  IconPosition,
  ModalButtons,
  NotificationTheme,
} from '@fcom/ui-components/index';
import { LanguageService } from '@fcom/ui-translate/index';
import { RootPaths, unique } from '@fcom/core/index';
import {
  CheckInBoardingPassByEmailResponse,
  CheckInBoardingPassByEmailResponseStatus,
  FinnairBoundItem,
  FinnairOrder,
  FinnairPassengerCode,
} from '@fcom/dapi/api/models';
import { EMAIL_PATTERN, unsubscribe } from '@fcom/core/utils';
import { CommonBookingFeatureState } from '@fcom/common-booking/index';
import { MmbOrderService } from '@fcom/manage-booking-common/index';
import { ElementActions, ElementTypes, GaContext, ToasterMessage, ToasterService } from '@fcom/common/index';
import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { FeedbackService } from '@fcom/common/services/feedback/feedback.service';
import { GtmService } from '@fcom/common/gtm';
import { MANAGE_BOOKING_STEPS } from '@fcom/common-booking/manage-booking-config';
import { BoundType } from '@fcom/dapi/index';

import { getPassengers } from '../../../store/selectors/check-in.selector';
import { ManageBookingFeatureState } from '../../../interfaces';
import { FinnairPassengerItemWithApis } from '../interface/check-in.interface';
import { CheckInService } from '../../../services/check-in.service';
import { CarTrawlerClientID } from '../../../constants';

@Component({
  selector: 'fin-check-in-confirmation-route',
  templateUrl: 'check-in-confirmation-route.component.html',
  styleUrls: ['./check-in-confirmation-route.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckInConfirmationRouteComponent implements OnInit, OnDestroy {
  readonly ButtonPosition = ButtonPosition;
  readonly ButtonLayout = ButtonLayout;
  readonly IconPosition = IconPosition;
  readonly FinnairPassengerCode = FinnairPassengerCode;
  readonly ButtonMode = ButtonMode;
  readonly ButtonType = ButtonType;
  readonly ButtonTheme = ButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ModalButtons = ModalButtons;
  readonly BoundType = BoundType;
  readonly survey = QualtricsSurveys.CHECK_IN;

  emailForm: FormGroup<{ email: FormControl<string> }>;
  locations$: Observable<FinnairOrder['locations']>;
  passengerIds$: Observable<string[]>;
  bound$: Observable<FinnairBoundItem>;
  order$: Observable<FinnairOrder>;
  isCarrierConnect$: Observable<boolean>;
  showCarTrawlerWidget$: Observable<boolean>;
  uniquePassengerEmails$: Observable<string[]>;
  openSurvey = false;

  emailSendingInProgress$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showSendEmailModal$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  carTrawlerClientId = CarTrawlerClientID.MMB_CHECK_IN_CONFORMATION;

  private passengersWithCheckInInfo$: Observable<FinnairPassengerItemWithApis[]>;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private store$: Store<CommonBookingFeatureState & ManageBookingFeatureState>,
    private router: Router,
    private languageService: LanguageService,
    private formBuilder: FormBuilder,
    private mmbOrderService: MmbOrderService,
    private toasterService: ToasterService,
    private feedbackService: FeedbackService,
    private gtmService: GtmService,
    private checkInService: CheckInService
  ) {}

  ngOnInit(): void {
    this.feedbackService.setFeedbackWidgetVisibility(true);
    this.feedbackService.setSurvey(QualtricsSurveys.CHECK_IN);
    this.order$ = this.checkInService.getOrder();
    this.bound$ = this.checkInService.getBound();
    this.passengerIds$ = this.store$.pipe(getPassengers());
    this.isCarrierConnect$ = this.checkInService.isCarrierConnect();

    this.subscriptions.add(
      this.order$.pipe(take(1)).subscribe((order) => {
        const primaryPassenger = order.passengers.find(({ id }) => order.primaryTravelerId === id);

        this.emailForm = this.formBuilder.group({
          email: new FormControl(primaryPassenger?.email ?? '', [
            Validators.required,
            Validators.pattern(EMAIL_PATTERN),
          ]),
        });
      })
    );

    this.locations$ = this.order$.pipe(map(({ locations }) => locations));

    this.passengersWithCheckInInfo$ = combineLatest([this.order$, this.bound$, this.passengerIds$]).pipe(
      map(([order, bound, paxIds]) => {
        const checkInEligibilityForBound = order?.eligibilities?.checkIn?.find(
          (eligibility) => eligibility.id === bound.id
        );

        const checkinFlightEligibilities = checkInEligibilityForBound?.flightTravelerEligibility ?? {};

        return order.passengers
          .filter(({ id }) => paxIds.includes(id))
          .map((passenger) => {
            const eligibilities = Object.entries(checkinFlightEligibilities)
              .flatMap(([flightId, checkInFlightEligibility]) => {
                const eligibility = checkInFlightEligibility.find((e) => e.travelerIds.includes(passenger.id));

                return eligibility
                  ? {
                      ...eligibility,
                      flightId,
                    }
                  : undefined;
              })
              .filter(Boolean);

            return {
              ...passenger,
              eligibilities,
            };
          });
      })
    );

    this.uniquePassengerEmails$ = this.passengersWithCheckInInfo$.pipe(
      map((passengers) => passengers.map((passenger) => passenger.email).filter(unique))
    );

    this.showCarTrawlerWidget$ = this.order$.pipe(
      map(
        (order) =>
          order?.eligibilities?.carRental?.find((eligibility) => {
            return eligibility.id === 'JOURNEY';
          })?.isAllowedToUse
      ),
      distinctUntilChanged()
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  sendEmail(): void {
    this.emailSendingInProgress$.next(true);
    this.trackElement('send-email', ElementActions.SUBMIT);

    this.subscriptions.add(
      combineLatest([this.order$, this.passengersWithCheckInInfo$])
        .pipe(
          take(1),
          switchMap(([order, passengers]) => {
            const flightIds = passengers.reduce((all, pax) => {
              const passengerFlightIds = pax.eligibilities
                .filter((e) => e.isAllowedToUseBoardingPass)
                .map((e) => e.flightId);

              return [...new Set([...all, ...passengerFlightIds])];
            }, [] as string[]);

            const travelerIds = passengers.reduce((all, pax) => {
              const eligibleTravelerIds = pax.eligibilities
                .filter((e) => e.isAllowedToUseBoardingPass)
                .flatMap((e) => e.travelerIds)
                .filter(unique);

              return [...new Set([...all, ...eligibleTravelerIds])];
            }, [] as string[]);

            return this.mmbOrderService.sendBoardingPass(order.id, order.hash, {
              flightIds,
              emailAddress: this.emailForm.controls.email.value,
              travelerIds,
            });
          })
        )
        .subscribe({
          next: (boardingPassResponse: CheckInBoardingPassByEmailResponse) => {
            const responseResults = Object.values(boardingPassResponse?.results ?? {}).flatMap((flightResults) =>
              Object.values(flightResults)
            );
            const allSuccess =
              responseResults.length > 0 &&
              responseResults.every((result) => result === CheckInBoardingPassByEmailResponseStatus.SUCCESS);
            const partialSuccess = responseResults.some(
              (result) => result === CheckInBoardingPassByEmailResponseStatus.SUCCESS
            );
            const message = allSuccess
              ? this.buildToasterMessage('success')
              : partialSuccess
                ? this.buildToasterMessage('partialSuccess')
                : this.buildToasterMessage('error', NotificationTheme.ALERT);
            this.toasterService.addMessageToQueue(message);
            this.emailSendingInProgress$.next(false);
            this.showSendEmailModal$.next(false);
          },
          error: () => {
            this.toasterService.addMessageToQueue(this.buildToasterMessage('error', NotificationTheme.ALERT));
            this.emailSendingInProgress$.next(false);
            this.showSendEmailModal$.next(false);
          },
        })
    );
  }

  redirectToMmb(): void {
    this.trackElement('navigate-to-mmb');
    this.router.navigateByUrl(
      `${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.VIEW.path}`
    );
  }

  openModal($event: Event): void {
    $event.stopPropagation();
    this.openSurvey = true;
  }

  private trackElement(elementNameSuffix: string, action = ElementActions.CLICK): void {
    this.gtmService.trackElement(
      `boarding-pass_${elementNameSuffix}`,
      GaContext.CHECK_IN,
      ElementTypes.BUTTON,
      undefined,
      action
    );
  }

  private buildToasterMessage(key: string, theme: ToasterMessage['theme'] = NotificationTheme.SUCCESS): ToasterMessage {
    return {
      id: 'boardingPass',
      theme,
      contentKey: `MMB.checkIn.confirmation.notifications.boardingPass.${key}`,
    };
  }
}
